/**
 * DESIGN SYSTEM NOTE: Hard-coded Tailwind colors in this file (bg-gray-50, border-gray-200, etc.)
 * are intentional and match approved Figma designs. Platform CSS variables will be updated
 * to match Figma in a future initiative, then this file will be refactored to use semantic tokens.
 * See PR #9637 for context on the Figma-first strategy.
 */

import { useContext } from 'react';
import { observer } from 'mobx-react';
import {
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { FireIcon } from '@heroicons/react/24/solid';
import { Window } from '@execonline-inc/browser-window.private';
import { Tooltip } from 'react-tooltip';
import { ClientExperienceResource, ProductCollectionResource } from '../../../Types';
import { fromNullable, isJust } from 'maybeasy';
import { findLinkBy } from '../../../../../../../../Fetch';
import { trackIntercomEvent } from '../../../../../../../../services/intercomTrackingService';
import { T, TranslationsContext, translation } from '../../../../../../../../Translations';
import { getOfferingTypeLabel, getOfferingTypeIcon } from './offeringTypeHelpers';
import {
  FUTURE_LICENSE_TINT_CLASSES,
  LicenseStarts,
  Starts,
} from '../../../LicenseTracker/FutureLicense';

interface Props {
  experience: ClientExperienceResource;
  isSelected: boolean;
  onSelect: () => void;
  productCollection: ProductCollectionResource;
  index: number;
  isAdded: boolean;
  onToggle: () => void;
}

function EnhancedCourseCard({
  experience,
  isSelected,
  onSelect,
  productCollection,
  index,
  isAdded,
  onToggle,
}: Props) {
  const ts = useContext(TranslationsContext);
  const {
    title,
    featured,
    total_hours,
    offering_type,
    school_partner_name,
    product_license_type_ids,
    competency_names,
  } = experience.payload;

  const payload = experience.payload;

  // Extract Maybe values
  const hours = total_hours.getOrElseValue(0);
  const schoolName = school_partner_name || '';
  const schoolLogoUri = payload.school_partner_logo_url
    .orElse(() => fromNullable(experience.links.find((l) => l.rel === 'logo-on-white')?.href))
    .getOrElseValue('');
  const offeringType = offering_type;
  const description = payload.description.getOrElseValue('');
  const competencies = competency_names || [];

  // Check if unlicensed
  const isUnlicensed = product_license_type_ids.length === 0;

  // Check if PDF download is available
  const hasPdfDownload = findLinkBy({ rel: 'download-overview-pdf' }, experience.links).isJust();

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Course from Card', {
      courseId: experience.payload.id,
      courseTitle: title,
      action: isAdded ? 'remove' : 'add',
    });
    onToggle();
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackIntercomEvent('Builder V2 - Course Catalog - Download PDF from Card', {
      courseId: experience.payload.id,
      courseTitle: title,
    });
    findLinkBy({ rel: 'download-overview-pdf' }, experience.links).map((link) => {
      Window.openWindow(link.href, '_blank');
    });
  };

  const handleCardClick = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Select Course Card', {
      courseId: experience.payload.id,
      courseTitle: title,
    });
    onSelect();
  };

  return (
    <div
      className={`w-full text-left p-4 rounded-lg transition-all group cursor-pointer border ${
        isSelected ? 'border-border bg-[#F5F8F9] shadow-sm' : 'border-transparent hover:bg-gray-50'
      }`}
      onClick={handleCardClick}
      data-intercom-target="builder-v2-course-card"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 style={{ fontWeight: 'var(--font-weight-medium)' }}>{title}</h4>
            {isUnlicensed && (
              <span
                className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded"
                style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
                title={translation(
                  "This course is not included in your organization's current licenses. You can add it to preview, but learners won't have access without purchasing the appropriate license.",
                )(ts)}
              >
                <T kind="Unlicensed" />
              </span>
            )}
            {payload.license_starts_on
              .map((startsOn) => (
                <span
                  className={`px-2 py-0.5 border rounded ${FUTURE_LICENSE_TINT_CLASSES}`}
                  style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
                  data-intercom-target="builder-v2-course-card-future-license"
                >
                  <LicenseStarts startsOn={startsOn} />
                </span>
              ))
              .getOrElseValue(<></>)}
          </div>
        </div>
        <div
          className={`flex items-center gap-1 transition-opacity ${
            isSelected || isAdded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <button
            onClick={handleToggle}
            className={`p-1.5 rounded transition-colors ${
              isAdded
                ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            aria-label={translation(isAdded ? 'Remove course' : 'Add course')(ts)}
            data-intercom-target={isAdded ? 'builder-v2-remove-course' : 'builder-v2-add-course'}
            data-tooltip-id={`add-remove-${experience.payload.id}`}
          >
            {isAdded ? <CheckCircleIcon className="w-6 h-6" /> : <PlusIcon className="w-6 h-6" />}
          </button>
          <Tooltip
            id={`add-remove-${experience.payload.id}`}
            place="bottom"
            opacity={1}
            style={{
              backgroundColor: 'var(--foreground)',
              borderRadius: '8px',
              padding: '8px 12px',
              zIndex: 1000,
            }}
          >
            <p className="text-white" style={{ fontSize: 'var(--text-xs)' }}>
              <T kind="Add to this section" />
            </p>
          </Tooltip>

          {hasPdfDownload && (
            <>
              <button
                onClick={handleDownload}
                className="p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground rounded transition-colors"
                aria-label={translation('Download course overview')(ts)}
                data-intercom-target="builder-v2-download-course-pdf"
                data-tooltip-id={`download-${experience.payload.id}`}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
              </button>
              <Tooltip
                id={`download-${experience.payload.id}`}
                place="bottom"
                opacity={1}
                style={{
                  backgroundColor: 'var(--foreground)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  zIndex: 1000,
                }}
              >
                <p className="text-white" style={{ fontSize: 'var(--text-xs)' }}>
                  <T kind="Download PDF" />
                </p>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {/* Featured first */}
        {isJust(featured) && (
          <span
            className="flex items-center gap-1"
            style={{ fontSize: 'var(--text-xs)', color: '#d97706' }}
          >
            <FireIcon className="w-3 h-3" />
            <T kind="Featured" />
          </span>
        )}
        {/* School logo and name */}
        {isJust(featured) && schoolName && (
          <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
            •
          </span>
        )}
        {/* DVLP-7604: school logo is a logo-on-white asset - render it cleanly
            (no grey framing box or border) and a bit larger. */}
        {schoolLogoUri && (
          <img
            src={schoolLogoUri}
            alt={translation('School partner logo')(ts)}
            className="h-6 object-contain"
            style={{ maxWidth: '96px' }}
          />
        )}
        {schoolName && (
          <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
            {schoolName}
          </span>
        )}
        {/* Hours */}
        {hours > 0 && (
          <>
            <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
              •
            </span>
            <span
              className="flex items-center gap-1 text-muted-foreground"
              style={{ fontSize: 'var(--text-xs)' }}
            >
              <ClockIcon className="w-3 h-3" />
              <T kind="{{count}} hour" count={hours} />
            </span>
          </>
        )}
        {/* Offering type */}
        {offeringType && (
          <>
            <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
              •
            </span>
            <span
              className="flex items-center gap-1 text-muted-foreground"
              style={{ fontSize: 'var(--text-xs)' }}
            >
              {getOfferingTypeIcon(offeringType)
                .map(({ icon: IconComponent, color }) => (
                  <IconComponent key="offering-icon" color={color} size="xs" />
                ))
                .getOrElseValue(<></>)}
              {getOfferingTypeLabel(offeringType)}
            </span>
          </>
        )}
        {/* Unlicensed last */}
        {isUnlicensed && (
          <>
            <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
              •
            </span>
            <span
              className="flex items-center gap-1 text-muted-foreground"
              style={{ fontSize: 'var(--text-xs)' }}
            >
              <LockClosedIcon className="w-3 h-3" />
              <T kind="Unlicensed" />
            </span>
          </>
        )}
        {payload.license_starts_on
          .map((startsOn) => (
            <>
              <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
                •
              </span>
              <span
                className="flex items-center gap-1 text-muted-foreground"
                style={{ fontSize: 'var(--text-xs)' }}
              >
                <CalendarIcon className="w-3 h-3" />
                <Starts startsOn={startsOn} />
              </span>
            </>
          ))
          .getOrElseValue(<></>)}
      </div>

      {/* Description */}
      {description && (
        <p
          className="text-muted-foreground mb-3 line-clamp-2"
          style={{ fontSize: 'var(--text-sm)' }}
        >
          {description}
        </p>
      )}

      {/* Competencies */}
      {competencies.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {competencies.slice(0, 4).map((competency) => (
            <span
              key={competency}
              className="px-2 py-0.5 bg-muted border border-border rounded"
              style={{ fontSize: 'var(--text-xs)' }}
            >
              {competency}
            </span>
          ))}
          {competencies.length > 4 && (
            <span
              className="px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded"
              style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
            >
              +{competencies.length - 4} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default observer(EnhancedCourseCard);
